import { useCallback, useState } from "react";
import { useOperation } from "../useOperation/UseOperation";
import { updateTicket } from "../../pages/actions/actions";
import { useNavigate, useParams } from "react-router-dom";
import { ShowAlert } from "../../utils/showAlert";
import { UseGetStoreData } from "../getStoreData/UseGetStoreData";
import { currentDateTime } from "../../utils/helper";
import { useDispatch} from "react-redux";
import { ticketDataSuccess } from "../../redux/ticketSlice/ticketSlice";

enum ButtonType {
  NONE = '',
  INSURANCE_HOSPITAL = 'Insurance Hospital',
  DEMO = 'TestDemo',
  CUSTOMER = "Customer Test",
  INTERNAL = 'Internal Test',
  AMBULANCE = 'Ambulance Booking',
  SENIOR = 'Call By Senior',
  POCOCARE = 'Pococare Service Query',
}

export enum OptionType {
  NONE = '',
  INSURANCE = 'Insurance',
  HOSPITAL = 'Hospital'
}

export const useSelectOPtion = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data } = useOperation();
  const { getTicketData, getTicketUpdateData } = UseGetStoreData();
  const [selectedButton, setSelectedButton] = useState<ButtonType>(ButtonType.NONE);
  const [selectedOption, setSelectedOption] = useState<OptionType>(OptionType.NONE);
  const dispatch = useDispatch();

  const handleButtonClick = async (buttonKey: keyof typeof ButtonType): Promise<void> => {
    const buttonType = ButtonType[buttonKey];
    let status = true;

    // Only execute handlers if button is being selected (not deselected)
    if (selectedButton !== buttonType) {
      setSelectedOption(OptionType.NONE);
      switch (buttonKey) {
        case 'INSURANCE_HOSPITAL':
          break;
        case 'AMBULANCE':
          status = await handleAmbulanceFlow();
          break;
        case 'SENIOR':
          break;
        case 'DEMO':
        case 'CUSTOMER':
        case 'INTERNAL':
          status = await handleDemo(buttonType);
          break;
        case 'POCOCARE':
          break;
        default:
          break;
      }
    }

    if (!status) return;
    setSelectedButton(prev => prev === buttonType ? ButtonType.NONE : buttonType);
  };

  const handleAmbulanceFlow=()=>{
      setSelectedButton(ButtonType.AMBULANCE)
      return false
  }

  const handleOptionClick = (optionKey: keyof typeof OptionType) => {
    const optionType = OptionType[optionKey];
    setSelectedOption(prev => prev === optionType ? OptionType.NONE : optionType);
  };

  const createPayload = useCallback((type: string, status: string, message?: string, additionalData?: any) => ({
    ticketType: type,
    ticketStatus: status,
    ...(message ? {
      notes: [
        ...getTicketData?.notes,
        { date: currentDateTime(), message },
      ],
    } : {
      notes: getTicketData?.notes
    }),
    ...additionalData,
  }), [getTicketData?.notes]);

  const handleAmbulanceBooking = async (): Promise<boolean> => {
    if (!getTicketUpdateData?.patient) {
      ShowAlert("warning", "Please select the patient first to book an ambulance");
      return false;
    }
     if (!getTicketUpdateData?.poc) {
      ShowAlert("warning", "Please select the poc first");
      return false;
    }

    const payload = createPayload(
      "Ambulance Booking",
      "Work in progress",
      undefined,
      {
        patient: getTicketUpdateData?.patient,
        poc:getTicketUpdateData?.poc,
        currentMedicalStatusResponse: {
          ...getTicketUpdateData?.currentMedicalStatusResponse,
          isDoctorConsultationRequired: "No",
          isAmbulanceRequired: "Yes",
        },
      }
    );
    try {
     let response = await updateTicket(id!, payload);
    
     dispatch(ticketDataSuccess(response?.data));
      ShowAlert("success", "Ticket Updated Successfully");
      localStorage.setItem("currentTab", "ambulance");
      navigate("/logistics");
      return true;
    } catch (error) {
      console.error("Error updating ticket:", error);
      ShowAlert("error", "Failed to update ticket");
      return false;
    }
  };

  const handleDemo = useCallback(async (type: string): Promise<boolean> => {
    if (!id) {
      ShowAlert("warning", "Invalid Ticket");
      return false;
    }

    const payload = createPayload(type, "Completed", `Ticket Resolved-${type}`);
    try {
      await updateTicket(id, payload);
      ShowAlert("success", "Ticket Updated Successfully");
      navigate("/");
      return true;
    } catch (error) {
      console.error("Error updating ticket:", error);
      ShowAlert("error", "Failed to update ticket");
      return false;
    }
  }, [id, navigate, createPayload])

  const handleResolveTicket = useCallback(async () => {
    const payload = {
      ticketType: "Insurance/Hospital",
      ticketStatus: "Completed",
      notes: [
        ...getTicketData?.notes,
        {
          date: currentDateTime(),
          message: "Ticket Resolved-insurance/hospital query",
        },
      ],
    };
    if (id) {
      await updateTicket(id, payload);
      ShowAlert("success", "Ticket Updated Successfully");
      navigate("/");
    } else {
      ShowAlert("warning", "Invalid Ticket");
    }
  }, [getTicketData, id, navigate]);

  return {
    selectedOption,
    selectedButton,
    ButtonType,
    OptionType,
    data,
    handleDemo,
    handleButtonClick,
    handleResolveTicket,
    handleOptionClick,
    handleAmbulanceBooking
  };
};
